import { Product, Benefit } from '@/lib/supabase/products';

// Helper function to format arrays of benefits into a single string
const formatBenefits = (benefits?: Benefit[]): string => {
  if (!benefits || benefits.length === 0) return '';
  return benefits.map(b => `${b.title}: ${b.description}`).join(' | ');
};

// Helper function to format dimension object into a string
const formatDimensions = (dimensions?: { length: number; width: number; height: number }): string => {
  if (!dimensions) return '';
  return `${dimensions.length}x${dimensions.width}x${dimensions.height}`;
};

// Helper function to escape and format text for CSV
const escapeCSV = (text: any): string => {
  if (text === null || text === undefined) return '';
  const stringValue = String(text);
  // If the value contains quotes, commas, or newlines, wrap in quotes and escape internal quotes
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

// Helper function to format date for export
const formatDateForExport = (date: string): string => {
  const dateObj = new Date(date);
  return dateObj.toISOString().slice(0, 10);
};

// Helper function to format CSV cell
const formatCSVCell = (cell: any): string => {
  return escapeCSV(cell);
};

export function prepareProductsForExport(products: Product[]): any[] {
  return products.map(product => {
    const {
      id,
      name,
      description,
      price,
      discount_price,
      category,
      sku,
      stock_quantity,
      is_stock_managed,
      created_at,
      updated_at,
    } = product;

    return {
      id,
      name,
      description: description || '',
      price: price || 0,
      discount_price: discount_price || '',
      category: category || '',
      sku: sku || '',
      stock_quantity: stock_quantity || 0, 
      is_stock_managed: is_stock_managed ? 'Oui' : 'Non',
      created_at,
      updated_at,
    };
  });
}

export const exportProductsToCSV = (products: Product[], selectedIds: string[] = []) => {
  // Si aucun identifiant sélectionné n'est fourni, exporter tous les produits
  const productsToExport = selectedIds.length > 0
    ? products.filter(product => selectedIds.includes(product.id))
    : products;

  if (productsToExport.length === 0) {
    console.error('Aucun produit à exporter');
    return;
  }

  // Définir les en-têtes complets du CSV avec les nouvelles colonnes
  const headers = [
    'ID',
    'Nom',
    'Description',
    'Aperçu',
    'Prix',
    'Prix soldé',
    'Stock',
    'Gestion du stock',
    'Seuil de stock bas',
    'Statut',
    'Catégorie',
    'Avantages',
    'Tags',
    'Poids',
    'Dimensions',
    'SKU',
    'Visible',
    'Créé le',
    'Modifié le',
    'Stock',
    'Devise'
  ];

  // Formater chaque produit en une ligne CSV avec toutes les données
  const rows = productsToExport.map(product => [
    product.id,
    product.name,
    product.description || '',
    product.overview || '',
    product.price,
    product.discount_price || '',
    product.stock_quantity || 0,
    product.is_stock_managed ? 'Oui' : 'Non',
    product.low_stock_threshold || 0,
    product.status || 'draft',
    product.category || '',
    formatBenefits(product.benefits),
    (product.tags || []).join(', '),
    product.weight || '',
    formatDimensions(product.dimensions),
    product.sku || '',
    product.is_visible ? 'Oui' : 'Non',
    product.created_at,
    product.updated_at,
    product.stock_quantity || 0,
    product.currency || 'DZD'
  ]);

  // Combiner les en-têtes et les lignes
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => escapeCSV(cell)).join(','))
  ].join('\n');

  // Créer un objet Blob avec le contenu CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Créer un lien de téléchargement et le déclencher
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `produits_export_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const generateProductsCSV = (products: Product[]) => {
  // Définir les en-têtes
  const headers = [
    'ID',
    'Nom',
    'Description',
    'Prix',
    'Prix réduit',
    'Catégorie',
    'SKU',
    'Stock',
    'Gestion du stock',
    'Seuil de stock bas',
    'Poids',
    'Dimensions',
    'Tags',
    'Visible',
    'Statut',
    'Créé le',
    'Mis à jour le'
  ];

  // Préparer les données
  const csvRows = products.map(product => {
    const dimensions = `${product.dimensions.length}x${product.dimensions.width}x${product.dimensions.height}`;
    const tags = product.tags ? product.tags.join(', ') : '';

    return [
      product.id,
      product.name,
      product.description,
      product.price.toString(),
      product.discount_price ? product.discount_price.toString() : '',
      product.category || '',
      product.sku || '',
      product.stock_quantity.toString(),
      product.is_stock_managed ? 'Oui' : 'Non',
      product.low_stock_threshold.toString(),
      product.weight ? product.weight.toString() : '',
      dimensions,
      tags,
      product.is_visible ? 'Oui' : 'Non',
      product.status,
      formatDateForExport(product.created_at),
      formatDateForExport(product.updated_at)
    ];
  });

  // Combiner les en-têtes et les données
  return [headers, ...csvRows].map(row => row.map(formatCSVCell).join(',')).join('\n');
};
